import { forwardRef, ReactNode } from 'react';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box, InputAdornment, SxProps, TextField, TextFieldProps, Theme } from '@mui/material';

import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';

export type TextInputProps = TextFieldProps & {
  icon?: ReactNode;
  InputPropsSx?: SxProps<Theme>;
  helperText?: ReactNode;
  label?: string;
  labelSx?: SxProps<Theme>;
  absoluteHelperText?: boolean;
  containerSx?: SxProps<Theme>;
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      icon,
      InputPropsSx = {},
      helperText,
      label,
      labelSx = {},
      absoluteHelperText = false,
      containerSx = {},
      ...props
    },
    ref,
  ) => {
    const getHelperText = (): ReactNode => {
      if (helperText) {
        if (props.error) {
          return (
            <>
              <ErrorOutlineRoundedIcon sx={{ fontSize: '16px' }} />
              {helperText}
            </>
          );
        }
        return helperText;
      }

      return undefined;
    };

    const textField = (
      <TextField
        {...props}
        placeholder={props.disabled ? '' : props.placeholder}
        inputRef={ref} // <— use forwarded ref here
        InputProps={{
          startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined,
          ...props.InputProps,
          sx: {
            backgroundColor: props.disabled ? COLORS.grey100 : COLORS.white,
            border: `1px solid ${COLORS.grey200}`,
            borderRadius: props.multiline ? '16px' : '100px',
            '.MuiInputBase-input': {
              padding: icon ? '10px 16px 10px 0' : '10px 16px',
              fontSize: '15px',
              lineHeight: '20px',
              height: '20px',
            },
            '.MuiInputBase-inputMultiline': {
              padding: 0,
            },
            '&.Mui-error': {
              borderColor: COLORS.error,
            },
            '& fieldset': { border: 'none' },
            ...InputPropsSx,
          },
        }}
        FormHelperTextProps={{
          sx: {
            color: props.error ? COLORS.error : COLORS.grey400,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginLeft: 0,
            fontSize: '12px',
            lineHeight: '17px',
            ...(absoluteHelperText && {
              position: 'absolute',
              top: '100%',
            }),
          },
        }}
        helperText={getHelperText()}
      />
    );

    if (label) {
      return (
        <Box sx={{ width: '100%', ...containerSx }}>
          <Text
            variant="inputSmall"
            sx={{
              marginBottom: '6px',
              color: props.disabled ? COLORS.grey250 : COLORS.textMain,
              ...labelSx,
            }}
          >
            {label}
          </Text>
          {textField}
        </Box>
      );
    }

    return textField;
  },
);

TextInput.displayName = 'TextInput';
export default TextInput;
